import { combineReducers, configureStore } from "@reduxjs/toolkit";
import mixpanel from "mixpanel-browser";
import createSagaMiddleware from "redux-saga";
import { all } from "redux-saga/effects";

import { apiSlice } from "api/api.slice";
import { LanguageCode } from "common/types/languageCode";
import type { RecursivePartial } from "common/types/recursivePartial";
import { readFromLocalStorage, readOidcUserFromLocalStorage } from "common/utilities/localStorage";
import { setThemeColorMeta } from "common/utilities/setThemeColorMeta";
import { initialState as authenticationInitialState } from "domains/authentication/reducers";
import { briefingGenerationSlice } from "domains/briefing/briefingGeneration.slice";
import { callsToActionSlice } from "domains/callsToAction/slice";
import { dimensionSlice } from "domains/dimension/dimension.slice";
import { domainsReducer } from "domains/domainsReducer";
import { domainsSagaSpawners } from "domains/domainsSagaSpawners";
import { initialState as incidentsInitialState } from "domains/incidents/reducer";
import { fetchTermsSuccess } from "domains/language/actions";
import { initialState as languageInitialState, languageReducer } from "domains/language/reducer";
import { sourceFilterSlice } from "domains/moderate/sources/sources.slice";
import { searchSlice } from "domains/search/search.slice";
import { initialState as themeInitialState } from "domains/theme/reducer";
import { topicSlice } from "domains/topic/topic.slice";
import { entitiesReducer } from "entities/entitiesReducer";
import { entitiesSagaSpawners } from "entities/entitiesSagaSpawners";
import { initialState as permissionsInitialState } from "entities/permissions/reducer";
import { userSlice } from "entities/user/user.slice";

mixpanel.init(import.meta.env.VITE_MIXPANEL_KEY);

const sagaMiddleware = createSagaMiddleware();

const dismissedCallsToAction = readFromLocalStorage("dismissedCallsToAction") ?? [];
const dismissedIncidents = readFromLocalStorage("DISMISSED_INCIDENTS_STORAGE_KEY") ?? [];
const language = readFromLocalStorage("language") ?? LanguageCode.EN;
const permissions = readFromLocalStorage("permissions");
const theme = readFromLocalStorage("theme");
const terms = readFromLocalStorage("terms") ?? [];

document.documentElement.lang = language;
if (theme !== undefined) setThemeColorMeta(theme);

const reducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  [callsToActionSlice.reducerPath]: callsToActionSlice.reducer,
  [dimensionSlice.reducerPath]: dimensionSlice.reducer,
  [topicSlice.reducerPath]: topicSlice.reducer,
  [userSlice.reducerPath]: userSlice.reducer,
  [briefingGenerationSlice.reducerPath]: briefingGenerationSlice.reducer,
  [sourceFilterSlice.reducerPath]: sourceFilterSlice.reducer,
  [searchSlice.reducerPath]: searchSlice.reducer,
  domains: domainsReducer,
  entities: entitiesReducer,
});

const preloadedState: RecursivePartial<Parameters<typeof reducer>[0]> = {
  ...(permissions !== undefined
    ? { entities: { permissions: { ...permissionsInitialState, ...permissions } } }
    : undefined),
  callsToAction: { dismissed: dismissedCallsToAction },
  domains: {
    authentication: { ...authenticationInitialState, idToken: readOidcUserFromLocalStorage()?.id_token },
    incidents: { ...incidentsInitialState, dismissed: dismissedIncidents },
    language: languageReducer(languageInitialState, fetchTermsSuccess({ terms }, { language })),
    theme: { ...themeInitialState, selected: theme },
  },
};

export const store = configureStore({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(apiSlice.middleware, sagaMiddleware),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  preloadedState,
  reducer,
});

function* saga() {
  yield all([...domainsSagaSpawners, ...entitiesSagaSpawners]);
}

sagaMiddleware.run(saga);
